import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Download, Save, X } from "lucide-react";
import Editor, { type OnMount } from "@monaco-editor/react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { assetContentUrl, getFile, listAssets, writeFile } from "@/api/constructor";
import { apiErrorMessage } from "@/lib/api";
import type { DraftAssetDto } from "@/types/constructor";

export type ViewSelection =
    | { kind: "file"; path: string }
    | { kind: "asset"; path: string };

interface Props {
    draftId: number;
    selection: ViewSelection | null;
    onClose: () => void;
}

function languageFor(path: string): string {
    const ext = path.slice(path.lastIndexOf(".") + 1).toLowerCase();
    switch (ext) {
        case "html":
        case "htm":
            return "html";
        case "css":
            return "css";
        case "js":
        case "mjs":
            return "javascript";
        case "ts":
        case "tsx":
            return "typescript";
        case "json":
            return "json";
        case "md":
            return "markdown";
        case "xml":
        case "svg":
            return "xml";
        default:
            return "plaintext";
    }
}

type AssetKind = "image" | "svg" | "video" | "audio" | "pdf" | "text" | "binary";

function classifyAsset(path: string, mime: string | null | undefined): AssetKind {
    const ext = path.slice(path.lastIndexOf(".") + 1).toLowerCase();
    const m = (mime || "").toLowerCase();
    if (ext === "svg" || m === "image/svg+xml") return "svg";
    if (m.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp", "avif", "ico", "bmp"].includes(ext)) return "image";
    if (m.startsWith("video/") || ["mp4", "webm", "mov", "ogv"].includes(ext)) return "video";
    if (m.startsWith("audio/") || ["mp3", "wav", "ogg", "oga", "m4a", "flac"].includes(ext)) return "audio";
    if (m === "application/pdf" || ext === "pdf") return "pdf";
    if (m.startsWith("text/")
        || ["json", "xml", "yml", "yaml", "txt", "md", "csv", "tsv", "log", "ini", "toml", "html", "htm", "css", "js", "mjs", "ts", "tsx"].includes(ext)) return "text";
    return "binary";
}

function formatBytes(n: number): string {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function Header({ path, onClose, children }: {
    path: string;
    onClose: () => void;
    children?: React.ReactNode;
}): React.ReactNode {
    return (
        <div className="flex items-center justify-between border-b border-border bg-muted/30 px-3 py-1.5 text-xs">
            <span className="truncate font-mono" title={path}>{path}</span>
            <div className="flex items-center gap-2">
                {children}
                <button
                    type="button"
                    aria-label="Close preview"
                    title="Close preview"
                    onClick={onClose}
                    data-testid="close-file-view"
                    className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                    <X className="h-3.5 w-3.5" />
                </button>
            </div>
        </div>
    );
}

export function FileView({ draftId, selection, onClose }: Props): React.ReactNode {
    if (!selection) {
        return (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                Select a file or asset to preview, or use the chat to ask the AI to make changes.
            </div>
        );
    }
    if (selection.kind === "file") {
        return <FileEditorPane draftId={draftId} path={selection.path} onClose={onClose} />;
    }
    return <AssetPreviewPane draftId={draftId} path={selection.path} onClose={onClose} />;
}

// --- File (editable Monaco) -----------------------------------------------

function FileEditorPane({ draftId, path, onClose }: { draftId: number; path: string; onClose: () => void }): React.ReactNode {
    const queryClient = useQueryClient();
    const fileQuery = useQuery({
        queryKey: ["draft", draftId, "file", path],
        queryFn: () => getFile(draftId, path),
    });
    const [draftContent, setDraftContent] = React.useState("");
    const [dirty, setDirty] = React.useState(false);
    const editorRef = React.useRef<Parameters<OnMount>[0] | null>(null);

    React.useEffect(() => {
        if (fileQuery.data) {
            setDraftContent(fileQuery.data.content);
            setDirty(false);
        }
    }, [fileQuery.data]);

    const saveMut = useMutation({
        mutationFn: () => writeFile(draftId, path, draftContent, fileQuery.data?.version),
        onSuccess: () => {
            setDirty(false);
            toast.success(`Saved ${path}`);
            queryClient.invalidateQueries({ queryKey: ["draft", draftId, "files"] });
            queryClient.invalidateQueries({ queryKey: ["draft", draftId, "file", path] });
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Save failed")),
    });

    const onMount: OnMount = (editor, monaco) => {
        editorRef.current = editor;
        editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
            if (dirty) saveMut.mutate();
        });
    };

    if (fileQuery.isLoading) {
        return (
            <div className="flex h-full flex-col">
                <Header path={path} onClose={onClose} />
                <div className="flex flex-1 items-center justify-center"><Spinner /></div>
            </div>
        );
    }
    if (fileQuery.isError) {
        return (
            <div className="flex h-full flex-col">
                <Header path={path} onClose={onClose} />
                <div className="flex flex-1 items-center justify-center text-sm text-destructive">
                    Failed to load {path}.
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-full flex-col">
            <Header path={path} onClose={onClose}>
                {dirty && <span className="text-amber-600 dark:text-amber-400">unsaved</span>}
                <Button
                    type="button"
                    size="sm"
                    variant="default"
                    disabled={!dirty || saveMut.isPending}
                    onClick={() => saveMut.mutate()}
                    data-testid="save-file"
                >
                    <Save className="h-3.5 w-3.5" />
                    Save
                </Button>
            </Header>
            <div className="min-h-0 flex-1">
                <Editor
                    height="100%"
                    path={path}
                    language={languageFor(path)}
                    value={draftContent}
                    theme="vs-dark"
                    onMount={onMount}
                    onChange={(v) => {
                        const next = v ?? "";
                        setDraftContent(next);
                        setDirty(next !== fileQuery.data?.content);
                    }}
                    options={{
                        fontSize: 13,
                        minimap: { enabled: false },
                        scrollBeyondLastLine: false,
                        automaticLayout: true,
                        wordWrap: "on",
                        tabSize: 2,
                        renderWhitespace: "boundary",
                    }}
                />
            </div>
        </div>
    );
}

// --- Asset (read-only routed by mime) -------------------------------------

function AssetPreviewPane({ draftId, path, onClose }: { draftId: number; path: string; onClose: () => void }): React.ReactNode {
    // Cheap: we already cache the asset list — pull metadata from there.
    const listQuery = useQuery({
        queryKey: ["draft", draftId, "assets"],
        queryFn: () => listAssets(draftId),
    });
    const asset: DraftAssetDto | undefined = (listQuery.data || []).find((a) => a.path === path);
    const url = assetContentUrl(draftId, path);
    const kind = classifyAsset(path, asset?.mime ?? null);

    const meta = asset
        ? <span className="text-muted-foreground">{asset.mime || "?"} · {formatBytes(asset.sizeBytes)}</span>
        : null;

    return (
        <div className="flex h-full flex-col">
            <Header path={path} onClose={onClose}>
                {meta}
                <a
                    href={url}
                    download={path.split("/").pop()}
                    className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                    title="Download"
                    aria-label={`Download ${path}`}
                >
                    <Download className="h-3.5 w-3.5" />
                </a>
            </Header>
            <div className="min-h-0 flex-1 overflow-auto bg-[#1e1e1e]" data-testid={`asset-view-${kind}`}>
                <AssetBody draftId={draftId} path={path} url={url} kind={kind} />
            </div>
        </div>
    );
}

function AssetBody({ draftId, path, url, kind }: {
    draftId: number;
    path: string;
    url: string;
    kind: AssetKind;
}): React.ReactNode {
    if (kind === "image" || kind === "svg") {
        return (
            <div className="flex h-full w-full items-center justify-center p-4">
                <img
                    src={url}
                    alt={path}
                    className="max-h-full max-w-full object-contain"
                    style={{ background:
                        "repeating-conic-gradient(#1f1f1f 0% 25%, #2a2a2a 0% 50%) 50% / 16px 16px" }}
                />
            </div>
        );
    }
    if (kind === "video") {
        return (
            <div className="flex h-full w-full items-center justify-center p-4">
                <video src={url} controls className="max-h-full max-w-full" />
            </div>
        );
    }
    if (kind === "audio") {
        return (
            <div className="flex h-full w-full items-center justify-center p-4">
                <audio src={url} controls />
            </div>
        );
    }
    if (kind === "pdf") {
        return <object data={url} type="application/pdf" className="h-full w-full" />;
    }
    if (kind === "text") {
        return <TextAssetView draftId={draftId} path={path} url={url} />;
    }
    return (
        <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-6 text-center text-sm text-muted-foreground">
            <span>No inline preview for this file type.</span>
            <a href={url} download={path.split("/").pop()} className="inline-flex items-center gap-1 text-foreground underline">
                <Download className="h-3.5 w-3.5" /> Download
            </a>
        </div>
    );
}

function TextAssetView({ draftId, path, url }: { draftId: number; path: string; url: string }): React.ReactNode {
    const textQuery = useQuery({
        queryKey: ["draft", draftId, "asset-text", path],
        queryFn: async () => {
            const r = await fetch(url, { credentials: "include" });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            return r.text();
        },
    });
    if (textQuery.isLoading) {
        return <div className="flex h-full items-center justify-center"><Spinner /></div>;
    }
    if (textQuery.isError) {
        return <div className="flex h-full items-center justify-center text-sm text-destructive">Failed to load asset.</div>;
    }
    return (
        <Editor
            height="100%"
            path={path}
            language={languageFor(path)}
            value={textQuery.data ?? ""}
            theme="vs-dark"
            options={{
                readOnly: true,
                fontSize: 13,
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                automaticLayout: true,
                wordWrap: "on",
                tabSize: 2,
                renderWhitespace: "boundary",
            }}
        />
    );
}
